import React, { useState, useEffect, useCallback } from 'react';
import { showToast } from '../../utils/helpers';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

const NotificationSettings = ({ user }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({
    newsletter: false,
    email_notification: true,
    whatsapp_notification: false,
    push_notifications: false
  });
  const [original, setOriginal] = useState(settings);

  const timeZone = user?.timeZone || 'Africa/Lagos (WAT)';

  const loadPreferences = useCallback(async () => {
    try {
      const { data } = await api.get(API_CONFIG.SETTINGS.NOTIFICATION_PREFERENCES);
      if (data.success) {
        setSettings(data.data);
        setOriginal(data.data);
      }
    } catch {
      showToast('Failed to load notification preferences', 'error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const handleToggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        newsletter: settings.newsletter,
        email_notification: settings.email_notification,
        whatsapp_notification: settings.whatsapp_notification
      };
      const { data } = await api.put(API_CONFIG.SETTINGS.NOTIFICATION_PREFERENCES, payload);
      if (data.success) {
        setOriginal(settings);
        showToast('Notification settings saved', 'success');
      } else {
        showToast(data.message || 'Failed to save', 'error');
      }
    } catch {
      showToast('An error occurred while saving', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setSettings(original);
    showToast('Changes cancelled', 'info');
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(original);

  const handlePushToggle = async () => {
    const enabling = !settings.push_notifications;

    if (enabling) {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        showToast('Push notifications are not supported in this browser', 'warning');
        return;
      }
      try {
        const { data } = await api.get(API_CONFIG.SETTINGS.VAPID_PUBLIC_KEY);
        const vapidKey = data?.publicKey;
        if (!vapidKey) {
          showToast('Push notifications are not configured on the server', 'warning');
          return;
        }

        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          showToast('Notification permission denied', 'warning');
          return;
        }

        const reg = await navigator.serviceWorker.ready;
        const existingSub = await reg.pushManager.getSubscription();
        if (existingSub) {
          await existingSub.unsubscribe();
        }
        const subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey)
        });

        await api.post(API_CONFIG.SETTINGS.PUSH_SUBSCRIBE, { subscription });
        setSettings((prev) => ({ ...prev, push_notifications: true }));
        setOriginal((prev) => ({ ...prev, push_notifications: true }));
        showToast('Push notifications enabled', 'success');
      } catch (err) {
        console.error('Push subscribe error:', err);
        showToast('Failed to enable push notifications', 'error');
      }
    } else {
      try {
        await api.delete(API_CONFIG.SETTINGS.PUSH_UNSUBSCRIBE);
        setSettings((prev) => ({ ...prev, push_notifications: false }));
        setOriginal((prev) => ({ ...prev, push_notifications: false }));
        showToast('Push notifications disabled', 'success');
      } catch {
        showToast('Failed to disable push notifications', 'error');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-2">Notification Preferences</h3>
          <p className="text-gray-600 mb-4 text-sm">
            Select the notifications you would like to receive. You cannot opt out of service messages.
          </p>

          <div className="space-y-3">
            <Toggle
              icon={
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
              }
              iconBg="bg-blue-100"
              title="Our newsletter"
              description="Important changes and updates"
              checked={settings.newsletter}
              onChange={() => handleToggle('newsletter')}
            />

            <Toggle
              icon={
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              }
              iconBg="bg-green-100"
              title="Email Notifications"
              description="Get updates through email"
              checked={settings.email_notification}
              onChange={() => handleToggle('email_notification')}
            />

            <Toggle
              icon={
                <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              }
              iconBg="bg-emerald-100"
              title="WhatsApp Notifications"
              description="Receive updates on WhatsApp"
              checked={settings.whatsapp_notification}
              onChange={() => handleToggle('whatsapp_notification')}
            />

            <Toggle
              icon={
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              }
              iconBg="bg-orange-100"
              title="Push Notifications"
              description="Get push notifications in your browser"
              checked={settings.push_notifications}
              onChange={handlePushToggle}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-2">Date & Time</h3>
          <p className="text-gray-600 mb-4 text-sm">Time zones and calendar display settings.</p>

          <div className="p-4 bg-gray-100 rounded-lg mb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-gray-500">Current Time Zone</p>
                <h4 className="font-semibold text-sm">{timeZone}</h4>
              </div>
            </div>
            {/* <button className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Change Time Zone
            </button> */}
          </div>

          <div className="p-4 bg-gray-100 rounded-lg">
            <h4 className="font-medium text-sm mb-3">Calendar Settings</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Date Format</span>
                <span className="text-xs font-medium">DD/MM/YYYY</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Time Format</span>
                <span className="text-xs font-medium">12-hour</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">Week Starts On</span>
                <span className="text-xs font-medium">Monday</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
        <button
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
        <button
          onClick={handleCancel}
          disabled={!hasChanges}
          className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const Toggle = ({ icon, iconBg, title, description, checked, onChange }) => (
  <div className="flex items-center justify-between p-3 bg-gray-100 rounded-lg">
    <div className="flex items-center gap-3">
      <div className={`flex items-center justify-center w-10 h-10 ${iconBg} rounded-lg`}>
        {icon}
      </div>
      <div className="flex-1">
        <h4 className="font-medium text-sm">{title}</h4>
        <p className="text-xs text-gray-600">{description}</p>
      </div>
    </div>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        onChange={onChange}
      />
      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
    </label>
  </div>
);

export default NotificationSettings;
